import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertCircle } from 'lucide-react';
import { errorStatusForLog } from '../lib/apiError';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  isChunkError: boolean;
  autoReloadIn: number | null;
  autoReloadSkipped: boolean;
}

const DEPLOY_BUST_KEY = '__deploy_bust';
const RELOAD_TRACKER_KEY = 'chunk_reload_tracker_v2';
const MAX_AUTO_RELOADS = 3;
const RELOAD_WINDOW_MS = 2 * 60 * 1000;

type ReloadTracker = { ts: number; count: number };

function isChunkLoadError(err: unknown): boolean {
  if (!err) return false;
  const msg = (err as any)?.message ?? String(err);
  return (
    /failed to fetch dynamically imported module/i.test(msg) ||
    /loading chunk/i.test(msg) ||
    /chunk.*failed/i.test(msg) ||
    /Importing a module script failed/i.test(msg)
  );
}

function getTracker(): ReloadTracker {
  try {
    const raw = sessionStorage.getItem(RELOAD_TRACKER_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as ReloadTracker;
      if (typeof parsed.count === 'number' && typeof parsed.ts === 'number') {
        if (Date.now() - parsed.ts < RELOAD_WINDOW_MS) {
          return parsed;
        }
      }
    }
  } catch {
    // ignore storage errors (private mode, etc.)
  }
  return { ts: Date.now(), count: 0 };
}

function recordReload(): void {
  try {
    const cur = getTracker();
    const next: ReloadTracker = {
      ts: cur.count === 0 ? Date.now() : cur.ts,
      count: cur.count + 1,
    };
    sessionStorage.setItem(RELOAD_TRACKER_KEY, JSON.stringify(next));
  } catch {
    // ignore
  }
}

function canAutoReload(): boolean {
  return getTracker().count < MAX_AUTO_RELOADS;
}

function deployBustReload(): void {
  try {
    recordReload();
  } catch {
    // ignore
  }
  try {
    const url = new URL(window.location.href);
    url.searchParams.set(DEPLOY_BUST_KEY, String(Date.now()));
    window.location.replace(url.toString());
  } catch {
    window.location.reload();
  }
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    isChunkError: false,
    autoReloadIn: null,
    autoReloadSkipped: false,
  };

  private countdownTimer: ReturnType<typeof setInterval> | null = null;

  public static getDerivedStateFromError(error: Error): State {
    const chunkErr = isChunkLoadError(error);
    const shouldCountdown = chunkErr && canAutoReload();
    return {
      hasError: true,
      error,
      isChunkError: chunkErr,
      autoReloadIn: shouldCountdown ? 5 : null,
      autoReloadSkipped: chunkErr && !shouldCountdown,
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error in React component tree:', errorStatusForLog(error));
  }

  public componentDidUpdate(prevProps: Props, prevState: State) {
    if (
      this.state.isChunkError &&
      this.state.autoReloadIn !== null &&
      prevState.autoReloadIn === null
    ) {
      this.countdownTimer = setInterval(() => {
        this.setState((s) => {
          if (s.autoReloadIn === null) return s;
          if (s.autoReloadIn <= 1) {
            if (this.countdownTimer) {
              clearInterval(this.countdownTimer);
              this.countdownTimer = null;
            }
            if (canAutoReload()) {
              deployBustReload();
              return { ...s, autoReloadIn: 0 };
            }
            // Already hit the reload limit — stop the countdown and show a
            // clear hint instead of silently disappearing.
            return { ...s, autoReloadIn: null, autoReloadSkipped: true };
          }
          return { ...s, autoReloadIn: s.autoReloadIn - 1 };
        });
      }, 1000);
    }
  }

  public componentWillUnmount() {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  }

  private handleManualRefresh = () => {
    // Manual refresh bypasses the safety limit — user explicitly wants it.
    deployBustReload();
  };

  public render() {
    if (this.state.hasError) {
      const { error, isChunkError, autoReloadIn, autoReloadSkipped } = this.state;
      const title = isChunkError ? 'Update in progress…' : 'Something went wrong';
      const summary = isChunkError
        ? 'We just deployed an update. Your browser is referencing files from the previous version.'
        : 'We\'re sorry, an unexpected error occurred while loading this page. Our team has been notified.';

      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
          <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-xl shadow-xl p-8 text-center border border-red-100 dark:border-red-900/30">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30 mb-6">
              <AlertCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{title}</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              {summary}
            </p>
            {isChunkError && autoReloadIn !== null && autoReloadIn > 0 && (
              <p className="text-xs text-indigo-600 dark:text-indigo-300 mb-4">
                Refreshing automatically in <strong>{autoReloadIn}</strong> second{autoReloadIn === 1 ? '' : 's'}…
              </p>
            )}
            {isChunkError && autoReloadSkipped && (
              <p className="text-xs text-amber-600 dark:text-amber-300 mb-4">
                Auto-refresh limit reached — please click the button below.
              </p>
            )}
            {error && (
               <div className="bg-gray-100 dark:bg-gray-900 rounded p-4 mb-6 overflow-auto text-left text-xs max-h-32 font-mono text-gray-700 dark:text-gray-300">
                 {error.toString()}
               </div>
            )}
            <button
              onClick={this.handleManualRefresh}
              className="w-full inline-flex justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              {isChunkError ? 'Refresh now' : 'Refresh Page'}
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

